// Admin Panel API Service
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Debug logging
console.log('🔍 API_BASE_URL:', API_BASE_URL);
console.log('🔍 REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

class AdminApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.baseUrl = API_BASE_URL; // Add this for consistency
  }

  // Get authentication token
  getAuthToken() {
    return localStorage.getItem('adminToken');
  }

  // Generic API call method with authentication
  async makeRequest(endpoint, options = {}) {
    const token = this.getAuthToken();
    
    if (!token) {
      throw new Error('Access token required');
    }

    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Handle token expiration
      if (response.status === 401) {
        // Try to refresh token
        try {
          const refreshToken = localStorage.getItem('adminRefreshToken');
          if (refreshToken) {
            const refreshResponse = await fetch(`${this.baseURL}/api/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken })
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              localStorage.setItem('adminToken', refreshData.data.accessToken);
              
              // Retry original request with new token
              config.headers['Authorization'] = `Bearer ${refreshData.data.accessToken}`;
              const retryResponse = await fetch(url, config);
              const retryData = await retryResponse.json();
              
              if (!retryResponse.ok) {
                throw new Error(retryData.error || `HTTP error! status: ${retryResponse.status}`);
              }
              
              return retryData;
            }
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Clear invalid tokens
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminRefreshToken');
          localStorage.removeItem('adminUser');
          // Redirect to login
          window.location.href = '/login';
          return;
        }
        
        // If refresh failed, redirect to login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/login';
        return;
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('Admin API Error:', error);
      throw error;
    }
  }

  // Get dashboard statistics
  async getDashboardStats() {
    return this.makeRequest('/api/dashboard-stats');
  }

  // Get all users
  async getUsers() {
    return this.makeRequest('/api/users');
  }

  // Get all tax forms
  async getTaxForms() {
    return this.makeRequest('/api/tax-forms');
  }

  // Get detailed tax form by ID
  async getTaxFormDetails(formId) {
    return this.makeRequest(`/api/tax-forms/${formId}`);
  }

  // Update tax form status and expected return
  async updateTaxFormStatus(formId, status, expectedReturn, paymentAmount, adminNotes, draftNotes) {
    return this.makeRequest(`/api/tax-forms/${formId}/status`, {
      method: 'PUT',
      body: JSON.stringify({
        status,
        expectedReturn,
        paymentAmount,
        adminNotes,
        ...(draftNotes !== undefined && { draftNotes })
      })
    });
  }

  // Get secure file for viewing
  async getSecureFileUrl(gcsPath) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      // Use admin backend for viewing (with decryption)
      const response = await fetch(`${this.baseURL}/admin/files/view/${encodeURIComponent(gcsPath)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get file: ${response.status}`);
      }
      
      // Return the response object directly
      return response;
    } catch (error) {
      console.error('Error getting file for viewing:', error);
      throw error;
    }
  }

  // Download single file (with decryption)
  async downloadFile(gcsPath) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      // Use admin backend for downloads
      const response = await fetch(`${this.baseURL}/admin/files/download/${encodeURIComponent(gcsPath)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  // Download all files for an application (ZIP)
  async downloadAllFiles(applicationId) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    try {
      // Use admin backend for download all
      const response = await fetch(`${this.baseURL}/admin/files/download-all/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download all files: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error downloading all files:', error);
      throw error;
    }
  }

  // Get secure file URL for downloading
  getSecureDownloadUrl(gcsPath) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    // Use the correct admin backend URL and properly encode the GCS path
    return `${this.baseURL}/admin/files/${encodeURIComponent(gcsPath)}/download?token=${token}`;
  }

  // Get all appointments with pagination and filters
  async getAppointments(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/appointments?${queryString}` : '/api/appointments';
    
    return this.makeRequest(endpoint);
  }

  // Update appointment status
  async updateAppointmentStatus(appointmentId, status, adminNotes = '') {
    return this.makeRequest('/api/appointments/status', {
      method: 'PUT',
      body: JSON.stringify({
        appointmentId,
        status,
        adminNotes
      })
    });
  }

  // Reschedule appointment with new date and time
  async rescheduleAppointment(appointmentId, newDate, newTime, adminNotes = '') {
    return this.makeRequest('/api/appointments/reschedule', {
      method: 'PUT',
      body: JSON.stringify({
        appointmentId,
        newDate,
        newTime,
        adminNotes
      })
    });
  }

  // Update admin notes for appointment
  async updateAppointmentNotes(appointmentId, adminNotes) {
    return this.makeRequest('/api/appointments/notes', {
      method: 'PUT',
      body: JSON.stringify({
        appointmentId,
        adminNotes
      })
    });
  }

  // Delete appointment
  async deleteAppointment(appointmentId) {
    return this.makeRequest(`/api/appointments/${appointmentId}`, {
      method: 'DELETE'
    });
  }

  // Get appointment statistics
  async getAppointmentStats() {
    return this.makeRequest('/api/appointments/stats');
  }

  // Get all payments
  async getPayments() {
    return this.makeRequest('/api/payments');
  }

  // Get all support requests
  async getSupportRequests() {
    return this.makeRequest('/api/support-requests');
  }

  // Get all feedback with pagination and filters
  async getFeedback(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/feedback?${queryString}` : '/api/feedback';
    
    return this.makeRequest(endpoint);
  }

  // Reply to feedback
  async replyToFeedback(feedbackId, reply, adminName = 'Admin') {
    return this.makeRequest('/api/feedback/reply', {
      method: 'POST',
      body: JSON.stringify({
        feedbackId,
        reply,
        adminName
      })
    });
  }

  // Update feedback status
  async updateFeedbackStatus(feedbackId, status) {
    return this.makeRequest('/api/feedback/status', {
      method: 'PUT',
      body: JSON.stringify({
        feedbackId,
        status
      })
    });
  }

  // Delete feedback
  async deleteFeedback(feedbackId) {
    return this.makeRequest(`/api/feedback/${feedbackId}`, {
      method: 'DELETE'
    });
  }

  // Delete user
  async deleteUser(userId) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'DELETE'
    });
  }

  // Delete tax form application
  async deleteTaxForm(formId) {
    return this.makeRequest(`/api/tax-forms/${formId}`, {
      method: 'DELETE'
    });
  }

  // Admin file upload methods (using admin backend on port 5001)
  async uploadReturn(applicationId, returnType, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('applicationId', applicationId);
    formData.append('returnType', returnType);

    const url = `${this.baseUrl}/admin/upload/return`;
    console.log('Upload URL:', url);
    console.log('Base URL:', this.baseUrl);
    console.log('Application ID:', applicationId);

    return fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: formData
    }).then(response => {
      console.log('Response status:', response.status);
      console.log('Response URL:', response.url);
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
      return response.json();
    });
  }

  async uploadDocument(applicationId, category, file, additionalPayload = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('applicationId', applicationId);
    formData.append('category', category);
    const { incomeSourceType, incomeAmount, incomeDescription, dependentId, dependentName, dependentDob, dependentSsn } = additionalPayload || {};
    if (incomeSourceType != null && incomeSourceType !== '') {
      formData.append('incomeSourceType', incomeSourceType);
    }
    if (incomeAmount != null && incomeAmount !== '') {
      formData.append('incomeAmount', incomeAmount);
    }
    if (incomeDescription != null && incomeDescription !== '') {
      formData.append('incomeDescription', incomeDescription);
    }
    if (dependentId != null && dependentId !== '') {
      formData.append('dependentId', dependentId);
    }
    if (dependentName != null && dependentName !== '') {
      formData.append('dependentName', dependentName);
    }
    if (dependentDob != null && dependentDob !== '') {
      formData.append('dependentDob', dependentDob);
    }
    if (dependentSsn != null && dependentSsn !== '') {
      formData.append('dependentSsn', dependentSsn);
    }
    const url = `${this.baseUrl}/admin/upload/document`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.getAuthToken()}` },
      body: formData
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Upload failed: ${response.status}`);
    }
    return response.json();
  }

  async getReturns(applicationId) {
    return this.makeRequest(`/admin/returns/${applicationId}`);
  }

  async downloadReturn(applicationId, returnType) {
    return this.makeRequest(`/admin/returns/${applicationId}/${returnType}`);
  }

  // Admin user management endpoints
  async getAvailablePages() {
    return this.makeRequest('/api/admin/available-pages');
  }

  async getAdminUsers() {
    return this.makeRequest('/api/admin-users');
  }

  async createAdminUser(userData) {
    return this.makeRequest('/api/admin-users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateAdminUser(userId, userData) {
    return this.makeRequest(`/api/admin-users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteAdminUser(userId) {
    return this.makeRequest(`/api/admin-users/${userId}`, {
      method: 'DELETE'
    });
  }

  // Email configuration endpoints
  async getEmailConfiguration() {
    return this.makeRequest('/api/email-configuration');
  }

  async saveEmailConfiguration(configData) {
    return this.makeRequest('/api/email-configuration', {
      method: 'PUT',
      body: JSON.stringify(configData)
    });
  }

  async testEmailConfiguration(configData) {
    return this.makeRequest('/api/email-configuration/test', {
      method: 'POST',
      body: JSON.stringify(configData)
    });
  }
}

export default new AdminApiService();
